import { ProgramType } from "#/lib/carrot-api/types/Benefit"
import { useReimbursements } from "#/services/user/hooks/useReimbursements"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { ExpenseState } from "#/utils/ExpenseState"
import { useSelector } from "react-redux"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import useCarrotPlan from "#/components/carrot-plans/hooks/useCarrotPlan"
import useCarrotPlanEmployeeSettings from "#/components/carrot-plans/hooks/useCarrotPlanEmployeeSettings"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { useBenefitEnrollment } from "#/components/context/enrollment/BenefitEnrollmentContext"
import { useCurrentUser } from "#/components/context/user/UserContext"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { BenefitAvailabilityStatus } from "#/types/benefitEnrollmentStatus"
import { usePreAuthEligibility } from "#/components/carrot-card-cost-share/state/usePreAuthEligibility"
import { useIsSprintsVisible } from "#/services/user/hooks/useSprintsQuery"

export function useSidebarCards() {
  const { status: benefitEnrollmentStatus, availabilityStatus } = useBenefitEnrollment()
  const { data: benefit, isLoading: isBenefitLoading } = useBenefit()
  const needsBenefitEnrollment = !!benefitEnrollmentStatus?.needsBenefitEnrollment
  const { data: memberHasACarrotPlan, isLoading: isCarrotPlanLoading } = useCarrotPlan()
  const { isInAppLockdown } = useCurrentUser()
  const subsidies = useSelector(getSubsidies)
  const hasUnlimitedBenefit = subsidies?.every((plan) => plan.hasUnlimitedBenefit)
  const { data: reimbursementsList = [] } = useReimbursements()
  const expensesNeedingMemberUploads = reimbursementsList.filter(
    (expense: { expenseState: string }) => expense.expenseState === ExpenseState.NEEDS_MEMBER_UPLOADS
  )
  const { isPartnerAccessingAccount } = useUserRole()
  const cpesQuery = useCarrotPlanEmployeeSettings()
  const cpRequestStatusQuery = useGetCarrotPlanRequestStatus()

  const { isVisible: isSprintsVisible, isLoading: isSprintsLoading } = useIsSprintsVisible()

  const isCarrotRxActive = benefit?.carrotRx.isCarrotRxActive
  const isCarrotPlanOnTheWay = cpRequestStatusQuery.data?.carrotPlanIsOnTheWay
  const isDoneForNow = cpesQuery.isDoneForNow

  const isCoreOrEngage = [ProgramType.CORE, ProgramType.WTW_ENGAGE].includes(benefit?.program?.type)
  const hasAtLeastOneExpenseNeedingMemberUploads =
    expensesNeedingMemberUploads && expensesNeedingMemberUploads?.length > 0

  const isPro = benefit?.program?.type === ProgramType.PRO
  const isProWithoutUnlimitedBenefit = isPro && !hasUnlimitedBenefit
  const isProWithUnlimitedBenefit = isPro && hasUnlimitedBenefit
  const isSupportCenterEnabled = benefit?.supportCenter?.enabled ?? false

  const preAuthEligibility = usePreAuthEligibility()

  const benefitRequiresEnrollment = [
    BenefitAvailabilityStatus.NEEDS_BENEFIT_ENROLLMENT,
    BenefitAvailabilityStatus.COMPANY_ADDED_DEDUCTIBLE
  ].includes(availabilityStatus)

  const benefitIsExpired = [BenefitAvailabilityStatus.ENROLLMENT_PLAN_EXPIRED].includes(availabilityStatus)
  const doesNotNeedBenefitEnrollment =
    !needsBenefitEnrollment || availabilityStatus === BenefitAvailabilityStatus.DOES_NOT_NEED_ENROLLMENT

  return {
    data: {
      showNeedsMemberUploads: isPro && !isPartnerAccessingAccount && hasAtLeastOneExpenseNeedingMemberUploads,
      showLimitedBenefitEnrollmentRequired:
        isProWithoutUnlimitedBenefit && !isInAppLockdown && benefitRequiresEnrollment,
      showLimitedBenefitEnrollmentExpired: isProWithoutUnlimitedBenefit && !isInAppLockdown && benefitIsExpired,
      showAvailableFunds:
        !hasAtLeastOneExpenseNeedingMemberUploads &&
        isProWithoutUnlimitedBenefit &&
        (isInAppLockdown || doesNotNeedBenefitEnrollment),
      showUnlimitedBenefitEnrollment: isProWithUnlimitedBenefit && needsBenefitEnrollment,
      showBenefitHighlights: memberHasACarrotPlan && !isCarrotPlanOnTheWay && !isDoneForNow,
      showHelpCenter: isCoreOrEngage && isSupportCenterEnabled,
      showSprints: isSprintsVisible,
      showCarrotRx: isCarrotRxActive && !isSprintsVisible,
      showCarrotCardPreAuth: preAuthEligibility.data?.eligibleForPreAuth && doesNotNeedBenefitEnrollment
    },
    isLoading:
      isBenefitLoading ||
      isCarrotPlanLoading ||
      cpRequestStatusQuery.isLoading ||
      preAuthEligibility.isLoading ||
      isSprintsLoading
  }
}
